import { supabase } from "@/lib/supabase";
import type { Signal, Subscription, SystemHealth } from "@/types/database";
import type { SparklinePoint } from "@/components/charts/SparklineChart";

/** Most recent signals, newest first. RLS gates detail tables, not this summary row. */
export async function fetchRecentSignals(limit = 10) {
  return supabase
    .from("signals")
    .select("id,strategy_version_id,asset_id,direction,confidence,timeframe,status,generated_at,expires_at,created_at")
    .order("generated_at", { ascending: false })
    .limit(limit)
    .returns<Signal[]>();
}

export interface SubscriptionWithPlanName extends Subscription {
  plans: { name: string } | null;
}

/** The caller's own subscription with plan name joined. RLS: owner-only read (migration 013). */
export async function fetchOwnSubscription(userId: string) {
  return supabase
    .from("subscriptions")
    .select("id,user_id,plan_id,status,billing_cycle,current_period_start,current_period_end,trial_ends_at,cancel_at,cancelled_at,created_at,updated_at, plans(name)")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle<SubscriptionWithPlanName>();
}

/** Worker health snapshot for the status strip. Readable by all authenticated users (migration 016). */
export async function fetchSystemHealth() {
  return supabase
    .from("system_health")
    .select("worker_name,status,last_check_at,latency_ms,details")
    .order("worker_name", { ascending: true })
    .returns<SystemHealth[]>();
}

/**
 * Count of signals that are actually still tradable now.
 * An expired signal remains status='active' until validation records its outcome,
 * so status alone is not sufficient to call a signal active in the UI.
 */
export async function fetchActiveSignalCount(): Promise<{ data: number | null; error: Error | null }> {
  const { count, error } = await supabase
    .from("signals")
    .select("id", { count: "exact", head: true })
    .eq("status", "active")
    .gt("expires_at", new Date().toISOString());

  if (error) return { data: null, error };
  return { data: count ?? 0, error: null };
}

/**
 * Daily signal generation counts for the last N days, shaped for
 * SparklineChart. Days with zero signals are included as 0, not omitted,
 * so the sparkline reflects true volume shape rather than skipping gaps.
 */
export async function fetchSignalCountSeries(
  days = 7,
): Promise<{ data: SparklinePoint[] | null; error: Error | null }> {
  const since = new Date();
  since.setDate(since.getDate() - (days - 1));
  since.setHours(0, 0, 0, 0);

  const { data, error } = await supabase
    .from("signals")
    .select("generated_at")
    .gte("generated_at", since.toISOString());

  if (error) return { data: null, error };

  const counts = new Map<string, number>();
  for (let i = 0; i < days; i++) {
    const d = new Date(since);
    d.setDate(d.getDate() + i);
    counts.set(d.toISOString().slice(0, 10), 0);
  }
  for (const row of data ?? []) {
    const key = row.generated_at.slice(0, 10);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const series: SparklinePoint[] = Array.from(counts.entries()).map(([date, count]) => ({
    x: date,
    y: count,
  }));

  return { data: series, error: null };
}

/**
 * Platform-aggregate win rate, weighted by total_signals, across
 * production strategy_versions only (shadow/candidate/retired excluded —
 * only live, customer-facing performance counts toward this number).
 */
export async function fetchAggregateWinRate(): Promise<{ data: number | null; error: Error | null }> {
  const { data, error } = await supabase
    .from("strategy_stats")
    .select("win_rate, total_signals, strategy_versions!inner(status)")
    .eq("strategy_versions.status", "production");

  if (error) return { data: null, error };
  if (!data || data.length === 0) return { data: null, error: null };

  let weightedSum = 0;
  let totalWeight = 0;
  for (const row of data) {
    const winRate = row.win_rate as number | null;
    const totalSignals = row.total_signals as number;
    if (winRate === null) continue;
    weightedSum += winRate * totalSignals;
    totalWeight += totalSignals;
  }

  if (totalWeight === 0) return { data: null, error: null };
  return { data: weightedSum / totalWeight, error: null };
}

/** Today's signal count vs yesterday's, for the delta shown on the StatCard. */
export async function fetchTodaysSignalCount(): Promise<{
  data: { today: number; yesterday: number } | null;
  error: Error | null;
}> {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  const [todayResult, yesterdayResult] = await Promise.all([
    supabase
      .from("signals")
      .select("id", { count: "exact", head: true })
      .gte("generated_at", startOfToday.toISOString()),
    supabase
      .from("signals")
      .select("id", { count: "exact", head: true })
      .gte("generated_at", startOfYesterday.toISOString())
      .lt("generated_at", startOfToday.toISOString()),
  ]);

  if (todayResult.error) return { data: null, error: todayResult.error };
  if (yesterdayResult.error) return { data: null, error: yesterdayResult.error };

  return {
    data: { today: todayResult.count ?? 0, yesterday: yesterdayResult.count ?? 0 },
    error: null,
  };
}

export interface TopStrategy {
  strategy_id: string;
  strategy_name: string;
  strategy_version_id: string;
  win_rate: number;
  total_signals: number;
}

/** Top N production strategies by win_rate, for the dashboard's Strategy Performance Summary. */
export async function fetchTopStrategies(
  limit = 3,
): Promise<{ data: TopStrategy[] | null; error: Error | null }> {
  const { data, error } = await supabase
    .from("strategy_stats")
    .select(
      "strategy_version_id, win_rate, total_signals, strategy_versions!inner(status, strategies!inner(id, name))",
    )
    .eq("strategy_versions.status", "production")
    .order("win_rate", { ascending: false })
    .limit(limit);

  if (error) return { data: null, error };

  const mapped: TopStrategy[] = (data ?? []).map((row) => {
    const version = row.strategy_versions as unknown as {
      strategies: { id: string; name: string };
    };
    return {
      strategy_id: version.strategies.id,
      strategy_name: version.strategies.name,
      strategy_version_id: row.strategy_version_id,
      win_rate: row.win_rate as number,
      total_signals: row.total_signals as number,
    };
  });

  return { data: mapped, error: null };
}
